import { Box, Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import * as React from "react";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { IEventUrgencyClassEditItem } from "ui/Scripts/source/models/eventUrgencyClass/eventUrgencyClassEditItem";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import * as store from "source/framework/customStore";
import { SaveDataObj } from "source/framework/saveData";
import * as eventService from "source/models/services/eventService";
import * as storeActions from "source/models/store/storeActions";
import { ColorPicker } from "source/components/framework/ColorPicker/ColorPicker";
import { Dialog } from "source/components/framework/Dialog/Dialog";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { DialogItemsNav } from "source/components/framework/Dialog/DialogItemsNav";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";

export interface IEventUrgencyClassDialogProp {
    classes?: string;
    id: string;
    companyId: string;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onOk: () => void;
    onClose: (refreshList: boolean) => void;
    onRemove: (ids: string[]) => Promise<ApiSuccess>;
    onRefreshList: () => Promise<void>;
}

export interface IEventUrgencyClassDialogState {
    id: string;
    rowId: string;
    isNew: boolean;
    code: string;
    comment: string;
    sortOrder: string;
    color: string;
    defaultUrgencyClass: boolean;
    activeState: number;
    item: IEventUrgencyClassEditItem;
}

export class EventUrgencyClassDialog extends React.Component<IEventUrgencyClassDialogProp, IEventUrgencyClassDialogState> {
    private orgStateHash: string = "";

    constructor(props: IEventUrgencyClassDialogProp) {
        super(props);
        this.state = {
            item: null,
            id: "",
            rowId: "",
            isNew: false,
            code: "",
            comment: "",
            sortOrder: "0",
            color: "",
            defaultUrgencyClass: false,
            activeState: 0,
        };
    }

    loadData = async (id: string) => {
        const props = this.props;
        const result = await AppUtils.callService(() => eventService.getEventUrgencyClassEdit(props.companyId, id), false);
        if (!result) return;
        const item = result.item;
        const state = {
            ...this.state,
            item,
            id: item.id,
            rowId: item.rowId,
            isNew: item.isNew(),
            code: item.code,
            comment: item.comment,
            sortOrder: item.sortOrder.toString(10),
            color: item.color,
            defaultUrgencyClass: item.defaultUrgencyClass,
            activeState: item.activeState
        };
        this.setState(state);
        this.orgStateHash = this.getSaveDataFromState(this.props, state).hash;
    };

    componentDidMount(): void {
        this.loadData(this.props.id);
    }

    async componentDidUpdate(prevProps: IEventUrgencyClassDialogProp, prevState: IEventUrgencyClassDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (prevProps.companyId === props.companyId && prevProps.id === props.id) return;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            props.onRefreshList();
        }
        this.loadData(props.id);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "code") {
            this.setState({ code: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        } else if (name === "sortOrder") {
            this.setState({ sortOrder: value });
        }
    };

    handleBlur = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "sortOrder") {
            this.setState({ sortOrder: value.toInteger().toString(10) });
        }
    };

    getSaveDataFromState = (props: IEventUrgencyClassDialogProp, state: IEventUrgencyClassDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("code", state.code);
        result.append("comment", state.comment);
        result.append("sortOrder", state.sortOrder);
        result.append("color", state.color);
        result.append("defaultUrgencyClass", state.defaultUrgencyClass);
        result.append("activeState", state.activeState);
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.NameMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => eventService.saveEventUrgencyClassEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId, isNew: false };
        this.orgStateHash = this.getSaveDataFromState(props, { ...state, ...stateChange }).hash;
        this.setState(stateChange);
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const result = await this.saveToDb();
        if (!result) return;
        if (!closeDialog) {
            this.loadData(result.id);
            this.props.onOk();
            return;
        }
        this.props.onClose(true);
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.isNew) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveEventUrgencyClassParameter, state.item.code), false) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await props.onRemove([state.id]);
            if (!result) return;
            props.onClose(true);
        }
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose(false);
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getSaveDataFromState(props, this.state).hash, this.orgStateHash,
            async () => !!(await this.saveToDb()),
            props.onRefreshList,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.item) return null;
        return (
            <Dialog
                id="event-urgency-class-dialog"
                maxWidth="sm"
                title={state.isNew ? Translations.AddEventUrgencyClass : (Translations.EventUrgencyClass + ": " + state.item.code)}
                overflowY="visible"
                body={
                    <>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Name} required margin={0}>
                                    <TextField type="text" name="code" title={Translations.Name} inputProps={{ maxLength: 100 }} disabled={false} value={state.code} required={true} onChange={this.handleChange} />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Description}>
                                    <TextField name="comment" title={Translations.Description} inputProps={{ maxLength: 1000 }} disabled={false} value={state.comment} onChange={this.handleChange} />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={6}>
                                <FormField title={Translations.SortOrder}>
                                    <TextField type="text" name="sortOrder" title={Translations.SortOrder} inputProps={{ maxLength: 6 }} disabled={false} value={state.sortOrder} required={true} onChange={this.handleChange} onBlur={this.handleBlur} />
                                </FormField>
                            </Grid>
                            <Grid item sm={6}>
                                <FormField title={Translations.Color} overflow="visible">
                                    <ColorPicker
                                        requiredColors={["#09753C", "#FFD100", "#E51A37"]}
                                        openAlignRight={true}
                                        value={state.color}
                                        onChange={color => this.setState({ color })}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={6}>
                                <FormField title="">
                                    <FormControlLabel
                                        label={Translations.Default}
                                        control={
                                            <Checkbox
                                                checked={state.defaultUrgencyClass}
                                                onChange={(_e, checked: boolean) => {
                                                    this.setState({ defaultUrgencyClass: checked });
                                                }}
                                            />
                                        }
                                    />
                                </FormField>
                            </Grid>
                            <Grid item sm={6}>
                                <FormField title="">
                                    <FormControlLabel
                                        label={Translations.InUse}
                                        control={
                                            <Checkbox
                                                checked={state.activeState > 0}
                                                onChange={(_e, checked: boolean) => {
                                                    this.setState({ activeState: checked ? 1 : 0 });
                                                }}
                                            />
                                        }
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            modifyUser={!state.isNew ? state.item.modifyUser : null}
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                        />
                        <DialogButtons
                            isNew={state.isNew}
                            onCancel={this.handleCancel}
                            onRemove={!state.isNew ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}/>
        );
    }
}